import {
	checkIfFileExists,
	checkIfFolderExists,
	getAllSubFolders,
	getConfig,
	getLogger,
} from '@hashnode/file-parsing'
import path from 'path'
import { ZodError } from 'zod'
import { formatZodErrors } from '../../format-zod-error'
import { isNonNullable } from '../../helpers'
import {
	apiReferenceVersionSchema,
	GuideVariant,
	guideVersionSchema,
	ParsedAPIReferenceVersionConfig,
	ParsedConfig,
	ParsedGuideVersionConfig,
} from '../../schemas'
import { ConfigError, FileError, FormattedZodError, GuideInformation } from '../../types'
import { getGuideConfig } from '../get-guide-config'
import {
	generateConfigFilePath,
	generateGuidesFolderPath,
	generateHashnodeConfigFilePath,
	getAllGuideConfigs,
} from '../path-helpers'

const logger = getLogger()

function validateGuideConfig(guideConfigPath: string): {
	error: FileError<ConfigError> | FormattedZodError[] | null
	config: ParsedConfig | null
} {
	try {
		const result = getGuideConfig(guideConfigPath)
		return {
			error: null,
			config: result,
		}
	} catch (error) {
		if (error instanceof ZodError) {
			return {
				error: formatZodErrors(guideConfigPath, error),
				config: null,
			}
		}

		return {
			error: {
				filePath: guideConfigPath,
				references: [{ message: (error as Error).message }],
			},
			config: null,
		}
	}
}

function validateVersionConfig({
	configFilePath,
	variant,
}: {
	configFilePath: string
	variant: GuideVariant
}): {
	config: ParsedGuideVersionConfig | ParsedAPIReferenceVersionConfig | null
	error: FileError<ConfigError> | FormattedZodError[] | null
} {
	try {
		// Check if config.ts exists in the subfolder
		if (!checkIfFileExists(configFilePath)) {
			return {
				config: null,
				error: { filePath: configFilePath, references: [{ message: 'Missing config.ts file' }] },
			}
		}

		// Parse the config file
		const configContent = getConfig({ filePath: configFilePath })

		if (!configContent) {
			return {
				config: null,
				error: {
					filePath: configFilePath,
					references: [{ message: 'Failed to read or parse config.ts' }],
				},
			}
		}

		if (variant === 'GUIDE') {
			const parsedConfig = guideVersionSchema.parse(configContent)

			return {
				config: parsedConfig,
				error: null,
			}
		} else {
			const parsedConfig = apiReferenceVersionSchema.parse(configContent)

			return {
				config: parsedConfig,
				error: null,
			}
		}
	} catch (error) {
		if (error instanceof ZodError) {
			return {
				error: formatZodErrors(configFilePath, error),
				config: null,
			}
		}

		return {
			config: null,
			error: {
				filePath: configFilePath,
				references: [{ message: (error as Error).message }],
			},
		}
	}
}

export function validateProjectStructure({
	ignoreHashnodeSettingsFile = false,
	rootDir,
}: {
	rootDir: string
	ignoreHashnodeSettingsFile?: boolean
}): {
	guideInformation: GuideInformation[]
	errorMap: Map<string, FileError<ConfigError> | FormattedZodError[]>
} {
	const errorMap: Map<string, FileError<ConfigError> | FormattedZodError[]> = new Map()

	// 1. Check if hashnode.json exists in the root of the project
	const hashnodePath = generateHashnodeConfigFilePath(rootDir)

	if (!ignoreHashnodeSettingsFile) {
		if (!checkIfFileExists(hashnodePath)) {
			logger.error('× Missing hashnode.ts in the root directory.')
			errorMap.set('hashnode.ts', {
				filePath: 'hashnode.ts',
				references: [],
			})
		}
	}

	// 2. Check each folder under guides to see if it contains a config.ts file
	const guidesPath = generateGuidesFolderPath(rootDir)

	// Check if guides directory exists
	if (!checkIfFolderExists(guidesPath)) {
		errorMap.set('/guides', {
			filePath: 'guides',
			references: [{ message: 'Missing guides folder path' }],
		})
		return {
			guideInformation: [],
			errorMap,
		}
	}

	// Read all subdirectories in guides
	const guideFolders = getAllGuideConfigs(guidesPath)

	// Validate config.ts file in each guide folder
	const guideFolderPaths = guideFolders.map((guideFolder) => path.join(guidesPath, guideFolder))

	const validGuideFolderPaths = guideFolderPaths
		.map((guideFolderPath) => {
			const result = validateGuideConfig(guideFolderPath)

			if (result.error) {
				errorMap.set(guideFolderPath, result.error)
			}

			return {
				guideFolderPath,
				config: result.config,
			}
		})
		.filter(isNonNullable)

	// Validate the config.ts for each version of the guide

	const guideInformation = validGuideFolderPaths
		.map(({ guideFolderPath, config }) => {
			// Check if there is a single subfolder
			const subfolders = getAllSubFolders(guideFolderPath)
			const defaultVersionSettings: string[] = []

			if (!config) {
				return null
			}

			const versionInformation = subfolders
				.map((subfolder) => {
					const versionFolderPath = path.join(guideFolderPath, subfolder)
					const configFilePath = generateConfigFilePath(versionFolderPath)

					const result = validateVersionConfig({ configFilePath, variant: config!.variant })
					if (result.config?.settings.isDefault) {
						defaultVersionSettings.push(configFilePath)
					}
					if (result.error) {
						errorMap.set(versionFolderPath, result.error)
					}

					if (!result.config) {
						return null
					}

					return {
						versionFolderPath,
						config: result.config,
					}
				})
				.filter(isNonNullable)

			if (defaultVersionSettings.length > 1) {
				errorMap.set(guideFolderPath, {
					filePath: guideFolderPath,
					references: [
						{
							message: 'More than one default version found',
							resolution: 'Exactly one default version is required',
							additionalInformation: defaultVersionSettings,
						},
					],
				})
			}

			if (defaultVersionSettings.length === 0) {
				errorMap.set(guideFolderPath, {
					filePath: guideFolderPath,
					references: [
						{
							message: 'No default version found',
							resolution: 'Exactly one default version is required',
						},
					],
				})
			}

			return {
				guideFolderPath,
				guideConfig: config,
				versionInformation,
			}
		})
		.filter(isNonNullable)

	return {
		guideInformation,
		errorMap,
	}
}
